import { LogOut, Share2, UserPlus, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import BackLink from '../components/BackLink'
import GroupBracketsTab from '../components/GroupBracketsTab'
import GroupTournamentsTab from '../components/GroupTournamentsTab'
import GroupYearLeaderboard from '../components/GroupYearLeaderboard'
import GroupEmailToggle from '../components/GroupEmailToggle'
import GroupInviteDialog from '../components/GroupInviteDialog'
import GroupMessageDialog from '../components/GroupMessageDialog'
import GroupMessagesTab from '../components/GroupMessagesTab'
import GroupQuietBanner from '../components/GroupQuietBanner'
import GroupMembersTab from '../components/GroupMembersTab'
import GroupPrizes from '../components/GroupPrizes'
import GroupSettings from '../components/GroupSettings'
import Layout from '../components/Layout'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardHeader } from '../components/ui/card'
import { SwipeableTabsContent, Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { useNotification } from '../contexts/NotificationContext'
import useGroupMessages from '../hooks/useGroupMessages'
import { useUser } from '../contexts/UserContext'
import { formatHumanDate } from '../lib/dateUtils'
import { canUseShareSheet, shareGroupInvite } from '../lib/groupInvite'
import { groupTypeLabel } from '../lib/groupTypes'
import {
  fetchGroupBrackets,
  fetchGroupDetail,
  fetchGroupMembers,
  joinGroup,
  leaveGroup,
  removeGroupMember,
} from '../services/groupService'

// Valid tabs and their URL slugs. The group page leads with its tournaments,
// laid out like Home (owner 2026-10-07); bracket-by-bracket management moved
// into Settings, so the old /brackets slug lands on Tournaments.
// Activity became Messages (owner 2026-10-12): owner messages, kudos and
// joins in one place. Old /activity links land on it.
const TAB_SLUGS = {
  tournaments: 'tournaments',
  leaderboard: 'leaderboard',
  messages: 'messages',
  members: 'members',
  prizes: 'prizes',
  settings: 'settings'
}

export default function GroupDetail() {
  const { id, tab } = useParams()
  const navigate = useNavigate()
  const { user } = useUser()

  const [group, setGroup] = useState(null)
  const [members, setMembers] = useState([])
  const [brackets, setBrackets] = useState([])
  const [loading, setLoading] = useState(true)
  const [joiningOrLeaving, setJoiningOrLeaving] = useState(false)
  const [removingMember, setRemovingMember] = useState(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const notify = useNotification()
  const messages = useGroupMessages(id)

  // Determine active tab from URL
  const slug = tab === 'activity' ? 'messages' : tab
  const activeTab = Object.values(TAB_SLUGS).includes(slug) ? slug : 'tournaments'

  const setActiveTab = (newTab) => {
    navigate(`/groups/${id}/${newTab}`, { replace: true })
  }

  useEffect(() => {
    loadGroupData()
  }, [id])

  const isAdmin = user?.role === 'admin'
  // Owner only (owner 2026-07-02): platform admins who are mere members
  // shouldn't see group Settings — transfer/delete belong to the owner.
  const canAccessSettings = !!group?.is_owner

  const visibleTabs = useMemo(() => {
    const tabs = ['tournaments', 'leaderboard', 'messages', 'members']
    if (group?.is_owner) tabs.push('prizes')
    if (canAccessSettings) tabs.push('settings')
    return tabs
  }, [group?.is_owner, canAccessSettings])

  // Protect prizes and settings tabs
  useEffect(() => {
    if (group && activeTab === 'prizes' && !group.is_owner) {
      navigate(`/groups/${id}/tournaments`, { replace: true })
    }
    if (group && activeTab === 'settings' && !canAccessSettings) {
      navigate(`/groups/${id}/tournaments`, { replace: true })
    }
  }, [group, activeTab, id, navigate, canAccessSettings])

  const loadGroupData = async () => {
    setLoading(true)

    try {
      const groupData = await fetchGroupDetail({ groupId: id })

      if (!groupData) {
        navigate('/groups')
        return
      }

      setGroup(groupData)

      try {
        const membersData = await fetchGroupMembers({ groupId: id })
        if (membersData) setMembers(membersData)
      } catch (_) { /* non-critical */ }

      try {
        const bracketsData = await fetchGroupBrackets({ groupId: id, userId: user?.id })
        if (bracketsData) setBrackets(bracketsData)
      } catch (_) { /* non-critical */ }

    } catch (err) {
      console.error('Error loading group:', err)
    } finally {
      setLoading(false)
    }
  }

  const reloadBrackets = async () => {
    try {
      const data = await fetchGroupBrackets({ groupId: id, userId: user?.id })
      if (data) setBrackets(data)
    } catch (_) { /* non-critical */ }
  }

  // Phones and the native app get the system share sheet; desktop, or a
  // share sheet that fails, gets the copy-link dialog.
  const handleInvite = async () => {
    if (canUseShareSheet()) {
      try {
        await shareGroupInvite({ groupId: id, groupName: group.name })
        return
      } catch (err) {
        console.error('Share sheet failed:', err)
      }
    }
    setShowShareModal(true)
  }

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    return formatHumanDate(date)
  }

  const handleJoinGroup = async () => {
    setJoiningOrLeaving(true)
    try {
      await joinGroup({ groupId: id })

      notify.success(`Successfully joined "${group.name}"!`)
      loadGroupData()
    } catch (err) {
      console.error('Error joining group:', err)
      notify.error(err.message || 'Failed to join group')
    } finally {
      setJoiningOrLeaving(false)
    }
  }

  const handleLeaveGroup = async () => {
    const confirmed = await notify.confirm({
      title: 'Leave Group',
      description: `Are you sure you want to leave "${group.name}"?`,
      confirmText: 'Leave',
      cancelText: 'Cancel'
    })

    if (!confirmed) return

    setJoiningOrLeaving(true)
    try {
      await leaveGroup({ groupId: id })

      notify.success(`Left "${group.name}"`)
      loadGroupData()
    } catch (err) {
      console.error('Error leaving group:', err)
      notify.error(err.message || 'Failed to leave group')
    } finally {
      setJoiningOrLeaving(false)
    }
  }

  const handleRemoveMember = async (memberId, memberName) => {
    const confirmed = await notify.confirm({
      title: 'Remove Member',
      description: `Are you sure you want to remove "${memberName}" from the group?`,
      confirmText: 'Remove',
      cancelText: 'Cancel'
    })

    if (!confirmed) return

    setRemovingMember(memberId)
    try {
      await removeGroupMember({ groupId: id, userId: memberId })

      notify.success(`Removed "${memberName}" from the group`)
      loadGroupData()
    } catch (err) {
      console.error('Error removing member:', err)
      notify.error(err.message || 'Failed to remove member')
    } finally {
      setRemovingMember(null)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading group...</p>
          </div>
        </div>
      </Layout>
    )
  }

  if (!group) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-muted-foreground">Group not found</p>
            <Button onClick={() => navigate('/groups')} className="mt-4">
              Back to Groups
            </Button>
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Group Header */}
        <div className="mb-8">
          <BackLink label="Groups" to="/groups" className="mb-4" />

          <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
            <div className="h-16 w-16 md:h-24 md:w-24 rounded-2xl bg-primary/10 flex items-center justify-center text-4xl md:text-5xl flex-shrink-0">
              <Users className="h-9 w-9 md:h-12 md:w-12 text-primary/60" />
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h1 className="text-3xl font-bold">{group.name}</h1>
                {group.is_owner && (
                  <Badge variant="secondary">Owner</Badge>
                )}
                {group.is_member && !group.is_owner && (
                  <Badge variant="secondary">Member</Badge>
                )}
              </div>
              {group.description && (
                <p className="text-muted-foreground mb-4 break-words">{group.description}</p>
              )}
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {group.owner_name && (
                  <span className="font-semibold text-foreground">Owner: {group.owner_name}</span>
                )}
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {group.member_count} member{group.member_count !== 1 ? 's' : ''}
                </span>
                {groupTypeLabel(group.group_type) && (
                  <Badge variant="outline">{groupTypeLabel(group.group_type)}</Badge>
                )}
                {group.is_member && !group.is_owner && (
                  <GroupEmailToggle
                    groupId={id}
                    optedOut={!!group.email_opted_out}
                    onChange={(optedOut) => setGroup({ ...group, email_opted_out: optedOut })}
                  />
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              {group.is_member && group.is_public && (!group.private || group.is_owner || isAdmin) && (
                <Button
                  variant="outline"
                  onClick={handleInvite}
                  className="w-full md:w-auto"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Invite people
                </Button>
              )}

              {group.is_member ? (
                !group.is_owner && (
                  <Button
                    variant="outline"
                    onClick={handleLeaveGroup}
                    disabled={joiningOrLeaving}
                    className="w-full md:w-auto"
                  >
                    {joiningOrLeaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                        Leaving...
                      </>
                    ) : (
                      <>
                        <LogOut className="h-4 w-4 mr-2" />
                        Leave Group
                      </>
                    )}
                  </Button>
                )
              ) : (
                group.is_public && (
                  <Button
                    variant="primaryNeon"
                    onClick={handleJoinGroup}
                    disabled={joiningOrLeaving}
                    className="w-full md:w-auto"
                  >
                    {joiningOrLeaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Joining...
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Join Group
                      </>
                    )}
                  </Button>
                )
              )}
            </div>
          </div>
        </div>

        {group.is_owner && (
          <GroupQuietBanner
            groupId={id}
            status={group.quiet_status}
            onRelaunched={() => setGroup({ ...group, quiet_status: null })}
          />
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <Card>
            <CardHeader className="pb-0">
              <TabsList variant="secondaryNav">
                <TabsTrigger value="tournaments" variant="secondaryNav">
                  Tournaments
                </TabsTrigger>
                <TabsTrigger value="leaderboard" variant="secondaryNav">
                  Leaderboard
                </TabsTrigger>
                <TabsTrigger value="messages" variant="secondaryNav">
                  Messages
                </TabsTrigger>
                <TabsTrigger value="members" variant="secondaryNav">
                  Members
                </TabsTrigger>
                {group.is_owner && (
                  <TabsTrigger value="prizes" variant="secondaryNav">
                    Prizes
                  </TabsTrigger>
                )}
                {canAccessSettings && (
                  <TabsTrigger value="settings" variant="secondaryNav">
                    Settings
                  </TabsTrigger>
                )}
              </TabsList>
            </CardHeader>

            <CardContent className="pt-6 px-3 sm:px-6">
              <SwipeableTabsContent tabs={visibleTabs} activeTab={activeTab} onTabChange={setActiveTab}>
              <TabsContent value="tournaments" className="mt-0">
                <GroupTournamentsTab
                  brackets={brackets}
                  groupId={id}
                  isOwner={group.is_owner}
                  onChanged={reloadBrackets}
                />
              </TabsContent>

              <TabsContent value="leaderboard" className="mt-0">
                <GroupYearLeaderboard groupId={id} brackets={brackets} />
              </TabsContent>

              <TabsContent value="messages" className="mt-0">
                <GroupMessagesTab
                  items={messages.items}
                  hiddenKeys={messages.hiddenKeys}
                  loading={messages.loading}
                  onSetHidden={messages.setHidden}
                  isOwner={!!group.is_owner}
                  canSendKudos={!!group.is_member}
                  onMessageGroup={() => setShowMessageModal(true)}
                  onGiveKudos={() => setActiveTab('members')}
                  formatTimeAgo={formatTimeAgo}
                />
              </TabsContent>

              {/* Members Tab */}
              <TabsContent value="members" className="mt-0">
                <GroupMembersTab
                  members={members}
                  canAccessSettings={canAccessSettings}
                  currentUserId={user?.id}
                  removingMember={removingMember}
                  onRemoveMember={handleRemoveMember}
                  formatTimeAgo={formatTimeAgo}
                  groupId={id}
                  canSendKudos={!!group.is_member}
                  onKudosSent={messages.reload}
                />
              </TabsContent>

              {/* Prizes Tab (Owner only) */}
              <TabsContent value="prizes" className="mt-0">
                {group.is_owner && (
                  <GroupPrizes
                    groupId={id}
                    isOwner={group.is_owner}
                    brackets={brackets}
                    members={members}
                  />
                )}
              </TabsContent>

              {/* Settings Tab (Owner or Admin only) */}
              <TabsContent value="settings" className="mt-0">
                {canAccessSettings && (
                  <GroupSettings
                    group={group}
                    members={members}
                    onGroupUpdated={(updatedGroup) => {
                      setGroup(updatedGroup)
                      loadGroupData()
                    }}
                    onGroupDeleted={() => navigate('/groups')}
                  />
                )}
                {canAccessSettings && (
                  <section className="mt-8 pt-6 border-t">
                    <h3 className="text-lg font-semibold mb-1">Brackets in this group</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Brackets you enter are added automatically. Remove any you don&apos;t want here.
                    </p>
                    <GroupBracketsTab
                      brackets={brackets}
                      groupId={id}
                      isOwner
                      onChanged={reloadBrackets}
                    />
                  </section>
                )}
              </TabsContent>
              </SwipeableTabsContent>
            </CardContent>
          </Card>
        </Tabs>
      </div>

      {group.is_owner && (
        <GroupMessageDialog
          open={showMessageModal}
          onOpenChange={setShowMessageModal}
          group={group}
          currentUserId={user?.id}
          onSent={() => {
            messages.reload()
            // Refresh last_email_at for the once-a-day check.
            fetchGroupDetail({ groupId: id }).then(g => g && setGroup(g)).catch(() => {})
          }}
        />
      )}

      <GroupInviteDialog
        open={showShareModal}
        onOpenChange={setShowShareModal}
        groupId={id}
        groupName={group.name}
      />
    </Layout>
  )
}
